/*
 * Home — H-Plate public landing page.
 *
 * Everything shown is real: photos of Dominica (credited in the footer),
 * screens from the shipping app, and the corridor list from the live
 * network. Keep it that way; no invented drivers, stats or reviews.
 */
import { Link } from "wouter";
import {
  ArrowRight,
  BellRing,
  Check,
  MapPinned,
  Share2,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { openDownloadDialog } from "@/components/DownloadAppDialog";
import StoreBadges from "@/components/marketing/StoreBadges";
import DeviceFrame from "@/components/marketing/DeviceFrame";
import VideoShowcase from "@/components/marketing/VideoShowcase";

/* ─── Content ─────────────────────────────────────────────────────────── */

const FACTS = [
  { value: "18", label: "Bus routes in the app" },
  { value: "100+", label: "Stops and villages" },
  { value: "EC$", label: "Official fares, shown first" },
  { value: "Free", label: "For riders and drivers" },
];

const TOUR = [
  {
    eyebrow: "Plan",
    title: "Know the fare before you ride",
    body: "Search any village or stop and see the route and the government-regulated fare in EC$ before you request. No surge, no haggling.",
    points: [
      "Fares follow the official schedule",
      "Your recent trips are one tap away",
      "Works for every corridor in the app",
    ],
    image: "/images/app-fare-search.webp",
    alt: "The H-Plate app showing a Where to? search and recent trips with their fares in EC$",
  },
  {
    eyebrow: "Ride",
    title: "Watch your bus arrive",
    body: "Once a driver accepts, follow the bus on the map as it comes to you. You see who is driving, the bus and its plate, and how far away it is.",
    points: [
      "Driver name, vehicle and H-plate",
      "Distance and minutes, updated live",
      "Call the driver or share your trip",
    ],
    image: "/images/app-live-tracking.webp",
    alt: "The H-Plate app tracking a bus on its way to a rider in Roseau, with the driver's details",
  },
  {
    eyebrow: "Explore",
    title: "The whole island on one map",
    body: "Switch between street, bus route, satellite and terrain views. See road hazards reported by other travellers, and places and events near your stop.",
    points: [
      "Four map styles, including satellite",
      "Landslide, flood and roadwork reports",
      "What's on near you while you wait",
    ],
    image: "/images/app-map-modes.webp",
    alt: "The H-Plate map style picker with explore, bus routes, satellite and terrain views",
  },
];

const SAFETY = [
  {
    icon: BellRing,
    title: "One-tap SOS",
    body: "Logs your GPS position, texts your emergency contact and sounds an alarm on the driver's phone. In a life-threatening emergency, call 911 first.",
  },
  {
    icon: Share2,
    title: "Share your trip",
    body: "Send family a link that shows your bus moving on the map. It never shows your name, and it stops working when the trip ends.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed drivers",
    body: "Drivers run their own licensed H-buses. They upload their driver's licence and a photo before taking riders, then their H-plate registration and insurance. Expired documents stop them going online.",
  },
  {
    icon: TriangleAlert,
    title: "Road hazard reports",
    body: "Riders and drivers flag landslides, flooding and roadwork. Others confirm or clear them as they pass, so the map stays current.",
  },
];

const ROUTES = [
  {
    name: "West Coast",
    ends: "Roseau to Portsmouth",
    via: ["Canefield", "Mahaut", "St Joseph", "Salisbury", "Colihaut"],
  },
  {
    name: "East Coast",
    ends: "Roseau to Castle Bruce",
    via: ["Goodwill", "Rosalie", "Grand Fond", "La Plaine", "Delices"],
  },
  {
    name: "Northeast",
    ends: "Roseau to Woodford Hill",
    via: ["Pond Casse", "Castle Bruce", "Kalinago Territory", "Marigot", "Wesley"],
  },
  {
    name: "South Coast",
    ends: "Roseau to Scotts Head",
    via: ["Newtown", "Loubiere", "Pointe Michel", "Soufriere"],
  },
  {
    name: "Southeast",
    ends: "Roseau to Petite Savanne",
    via: ["Loubiere", "Bellevue Chopin", "Grand Bay", "Bagatelle"],
  },
  {
    name: "North Coast",
    ends: "Portsmouth to Calibishie",
    via: ["Toucarie", "Clifton", "Capuchin", "Penville", "Vieille Case"],
  },
];

const LOCAL_ROUTES =
  "Imperial Road, the Airport Shuttle, Portsmouth North, Giraudel and Eggleston, Kingshill, Morne Prosper, Stock Farm, Cockrane, Warner, Upper Belfast, Campbell and Tete Morne";

const DRIVER_POINTS = [
  "Free to use. H-Plate takes no cut of your fares.",
  "Go online and offline whenever you like.",
  "Keeps working when signal drops in the hills.",
  "Riders pay you in cash, at the official fare.",
];

/* ─── Page ────────────────────────────────────────────────────────────── */

/**
 * The public landing page at h-plate.com.
 */
export default function Home() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 overflow-x-hidden">
      <Navbar overlay />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-[#04241b]">
        <picture>
          <source media="(max-width: 767px)" srcSet="/images/scotts-head-portrait-900.webp" />
          <source media="(max-width: 1440px)" srcSet="/images/scotts-head-1280.webp 1280w, /images/scotts-head-2400.webp 2400w" sizes="100vw" />
          <img
            src="/images/scotts-head-2400.webp"
            alt="Scotts Head village on the south coast of Dominica, with green hills above the bay"
            className="absolute inset-0 -z-10 h-full w-full object-cover"
            fetchPriority="high"
          />
        </picture>
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(90deg, rgba(3,30,22,0.92) 0%, rgba(3,30,22,0.78) 38%, rgba(3,30,22,0.25) 75%, rgba(3,30,22,0.15) 100%)",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 -z-10 md:hidden bg-[#031e16]/55" aria-hidden />

        <div className="container pt-32 pb-20 md:pt-40 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 text-white">
            <p className="inline-flex items-center gap-2 text-sm font-medium text-emerald-200">
              <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden />
              Now on the App Store and Google Play
            </p>
            <h1 className="mt-5 font-display text-[44px] leading-[1.04] sm:text-6xl lg:text-[72px] tracking-tight text-white">
              Dominica's buses,
              <br />
              in your pocket.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/80">
              Find your stop, see the official fare and watch your bus come to
              you. H-Plate connects riders with licensed H-bus drivers on
              routes across the island.
            </p>
            <StoreBadges className="mt-9" />
            <ul className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
              <li>Government-regulated fares</li>
              <li aria-hidden className="hidden sm:block text-white/30">/</li>
              <li>Pay the driver in cash</li>
              <li aria-hidden className="hidden sm:block text-white/30">/</li>
              <li>Free for riders and drivers</li>
            </ul>
          </div>
          <div className="lg:col-span-5">
            <DeviceFrame
              src="/images/app-welcome.webp"
              alt="The H-Plate app's welcome screen, showing Trafalgar Falls and the fare from Roseau"
              widthClass="w-[240px] sm:w-[280px] lg:w-[300px]"
              eager
            />
          </div>
        </div>
      </section>

      {/* FACTS */}
      <section className="border-b border-slate-200">
        <div className="container">
          <dl className="grid grid-cols-2 md:grid-cols-4">
            {FACTS.map((fact, i) => (
              <div
                key={fact.label}
                className={`py-8 md:py-10 px-4 text-center ${i % 2 === 1 ? "border-l border-slate-200" : ""} ${
                  i >= 2 ? "border-t md:border-t-0 border-slate-200" : ""
                } ${i === 2 ? "md:border-l" : ""}`}
              >
                <dt className="sr-only">{fact.label}</dt>
                <dd className="font-display text-4xl md:text-[44px] leading-none text-emerald-900">{fact.value}</dd>
                <dd className="mt-2 text-sm text-slate-500">{fact.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <VideoShowcase />

      {/* PRODUCT TOUR */}
      <section id="features" className="scroll-mt-20 py-20 md:py-28">
        <div className="container">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700">The app</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
              Made for how Dominica really gets around
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              Buses on set routes, the fare the Government sets, and a map that
              knows the island. Here is what you get when you open H-Plate.
            </p>
          </div>

          <div className="mt-16 md:mt-20 space-y-20 md:space-y-28">
            {TOUR.map((item, i) => (
              <div key={item.title} className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
                <div className={i % 2 === 1 ? "md:order-2" : ""}>
                  <div className="relative rounded-[2rem] bg-[#eef5f1] py-12 md:py-16">
                    <DeviceFrame src={item.image} alt={item.alt} widthClass="w-[230px] sm:w-[260px]" />
                  </div>
                </div>
                <div className={i % 2 === 1 ? "md:order-1" : ""}>
                  <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700">{item.eyebrow}</p>
                  <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-slate-900">{item.title}</h3>
                  <p className="mt-4 text-lg leading-relaxed text-slate-600">{item.body}</p>
                  <ul className="mt-6 space-y-3">
                    {item.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-slate-700">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.5} />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAFETY */}
      <section className="bg-[#04241b] text-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-300">Safety</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-white">
              Safer on every trip
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-white/70">
              The tools you want close by when you're out on the road, for you
              and for the people waiting at home.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
            {SAFETY.map((item) => (
              <div key={item.title} className="border-t border-white/15 pt-6">
                <item.icon className="h-6 w-6 text-emerald-300" strokeWidth={1.75} />
                <h3 className="mt-4 font-sans text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-white/65">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROUTES */}
      <section id="routes" className="scroll-mt-20 py-20 md:py-28 bg-[#f7f6f2]">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700">Routes</p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
                From the harbour to the hills
              </h2>
            </div>
            <p className="lg:col-span-5 text-lg leading-relaxed text-slate-600">
              H-buses run on set routes, so the app works the way the island
              already does. These are the main corridors.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
            {ROUTES.map((route) => (
              <div key={route.name} className="bg-white p-7">
                <div className="flex items-center gap-2 text-emerald-700">
                  <MapPinned className="h-4 w-4" strokeWidth={2} />
                  <p className="text-sm font-semibold">{route.name}</p>
                </div>
                <h3 className="mt-3 font-display text-2xl tracking-tight text-slate-900">{route.ends}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
                  via {route.via.join(", ")}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-3xl text-[15px] leading-relaxed text-slate-600">
            <span className="font-semibold text-slate-800">Plus 12 local routes:</span> {LOCAL_ROUTES}.
          </p>
        </div>
      </section>

      {/* DRIVERS */}
      <section id="drivers" className="scroll-mt-20 py-20 md:py-28">
        <div className="container grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <img
              src="/images/portsmouth-bay-street-1200.webp"
              alt="Bay Street in Portsmouth, Dominica, with a green and white building on the corner"
              width={1200}
              height={1500}
              loading="lazy"
              decoding="async"
              className="w-full max-h-[620px] object-cover rounded-[2rem]"
            />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700">For H-bus drivers</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
              See riders waiting on your route
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              You run your own bus. Go online on your corridor and H-Plate shows
              you riders who want a bus, and where to pick them up.
            </p>
            <ul className="mt-7 space-y-3.5">
              {DRIVER_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3 text-slate-700">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.5} />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={() => openDownloadDialog("driver")}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-emerald-700 text-white font-semibold border-0 cursor-pointer hover:bg-emerald-800 transition-colors"
              >
                Get the driver app
                <ArrowRight className="h-4 w-4" />
              </button>
              <Link href="/support" className="text-[15px] font-semibold text-emerald-800 hover:text-emerald-900 no-underline">
                Questions? Contact support
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-20 md:pb-28">
        <div className="container">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#04241b] px-6 py-14 md:px-16 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <h2 className="font-display text-4xl md:text-5xl tracking-tight text-white">
                  Your next bus is a tap away
                </h2>
                <p className="mt-4 max-w-xl text-lg leading-relaxed text-white/70">
                  Download H-Plate free on iPhone or Android. Sign up with your
                  email and you're ready to ride.
                </p>
              </div>
              <div className="lg:col-span-5 lg:flex lg:justify-end">
                <StoreBadges />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
